import { memo } from "react"
import { FiDollarSign, FiEye, FiTrash2 } from "react-icons/fi"

import type { EnrollmentResponse } from "@/types/enrollment"
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface EnrollmentCardProps {
  enrollment: EnrollmentResponse
  onEdit?: (enrollment: EnrollmentResponse) => void
  onDelete?: (enrollment: EnrollmentResponse) => void
  onView?: (enrollment: EnrollmentResponse) => void
  onRecordPayment?: (enrollment: EnrollmentResponse) => void
  showRecordPayment?: boolean
}

const EnrollmentCard = memo(
  ({
    enrollment,
    onEdit,
    onDelete,
    onView,
    onRecordPayment,
    showRecordPayment = false,
  }: EnrollmentCardProps) => {
    const titleValue = enrollment.student_id ?? enrollment.id

    const subtitleValue = enrollment.class_template_id ?? undefined

    // Payment calculations from nested invoice
    const amountDue = enrollment.invoice?.amount_due ?? 0
    const amountPaid = enrollment.invoice?.amount_paid ?? 0
    const outstandingBalance = amountDue - amountPaid

    // Format currency
    const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`

    // Payment status badge color
    const getPaymentStatusColor = (status: string | null | undefined) => {
      switch (status) {
        case "paid":
          return "green"
        case "partial":
          return "yellow"
        case "refunded":
          return "purple"
        default:
          return "red"
      }
    }

    return (
      <Stack
        borderWidth="1px"
        borderRadius="lg"
        padding={4}
        gap={4}
        bg="bg.surface"
        shadow="sm"
      >
        <Flex justify="space-between" align="flex-start">
          <Stack gap={1} flex="1">
            <Heading size="sm">{titleValue}</Heading>
            {subtitleValue !== undefined && (
              <Text color="fg.muted" fontSize="sm">
                {subtitleValue || "No description"}
              </Text>
            )}
          </Stack>
          <Badge
            colorScheme={getPaymentStatusColor(
              enrollment.invoice?.payment_status,
            )}
          >
            {enrollment.invoice?.payment_status?.toUpperCase() || "PENDING"}
          </Badge>
        </Flex>

        {/* Payment Summary Section */}
        {amountDue > 0 && (
          <Box
            p={3}
            bg={outstandingBalance > 0 ? "orange.50" : "green.50"}
            borderRadius="md"
            borderWidth="1px"
            borderColor={outstandingBalance > 0 ? "orange.200" : "green.200"}
          >
            <Stack gap={2} fontSize="sm">
              <Flex justify="space-between">
                <Text fontWeight="medium">Amount Due:</Text>
                <Text fontWeight="bold">{formatCurrency(amountDue)}</Text>
              </Flex>
              <Flex justify="space-between">
                <Text fontWeight="medium">Amount Paid:</Text>
                <Text fontWeight="bold" color="green.600">
                  {formatCurrency(amountPaid)}
                </Text>
              </Flex>
              {outstandingBalance > 0 && (
                <Flex justify="space-between" pt={2} borderTopWidth="1px">
                  <Text fontWeight="bold" color="orange.700">
                    Outstanding Balance:
                  </Text>
                  <Text fontWeight="bold" fontSize="lg" color="orange.700">
                    {formatCurrency(outstandingBalance)}
                  </Text>
                </Flex>
              )}
              {enrollment.invoice?.payment_method && (
                <Flex justify="space-between" fontSize="xs" color="fg.subtle">
                  <Text>Payment Method:</Text>
                  <Text textTransform="capitalize">
                    {enrollment.invoice.payment_method.replace("_", " ")}
                  </Text>
                </Flex>
              )}
            </Stack>
          </Box>
        )}

        <Stack gap={1} fontSize="xs" color="fg.subtle">
          <Text>
            Created: {new Date(enrollment.created_at).toLocaleString()}
          </Text>
          <Text>
            Updated: {new Date(enrollment.updated_at).toLocaleString()}
          </Text>
        </Stack>

        <Separator />
        <Stack gap={2} fontSize="sm">
          <Flex
            key="student_id"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Student Id</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(enrollment.student_id, "str")}
            </Text>
          </Flex>
          <Flex
            key="class_template_id"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Class Template Id</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(enrollment.class_template_id, "str")}
            </Text>
          </Flex>
          <Flex
            key="session_id"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Session Id</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(enrollment.session_id, "str")}
            </Text>
          </Flex>
          <Flex key="status" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Status</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(enrollment.status, "str")}
            </Text>
          </Flex>
          <Flex
            key="waitlist_position"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Waitlist Position</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(enrollment.waitlist_position, "int")}
            </Text>
          </Flex>
          <Flex
            key="payment_status"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Payment Status</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(enrollment.invoice?.payment_status, "str")}
            </Text>
          </Flex>
          <Flex
            key="amount_due"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Amount Due</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(enrollment.invoice?.amount_due, "float")}
            </Text>
          </Flex>
          <Flex
            key="amount_paid"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Amount Paid</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(enrollment.invoice?.amount_paid, "float")}
            </Text>
          </Flex>
          <Flex
            key="payment_method"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Payment Method</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(enrollment.invoice?.payment_method, "str")}
            </Text>
          </Flex>
          <Flex
            key="payment_date"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Payment Date</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(enrollment.invoice?.payment_date, "datetime")}
            </Text>
          </Flex>
          <Flex
            key="invoice_number"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Invoice Number</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(enrollment.invoice?.invoice_number, "str")}
            </Text>
          </Flex>
          <Flex key="notes" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Notes</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(enrollment.notes, "str")}
            </Text>
          </Flex>
        </Stack>

        <Flex gap={2} justify="flex-end" wrap="wrap">
          {showRecordPayment && onRecordPayment && outstandingBalance > 0 && (
            <Button
              size="sm"
              colorScheme="green"
              onClick={() => onRecordPayment(enrollment)}
            >
              <FiDollarSign /> Record Payment
            </Button>
          )}
          {onView && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onView(enrollment)}
            >
              <FiEye /> View
            </Button>
          )}
          {onEdit && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={() => onEdit(enrollment)}
            >
              Edit
            </Button>
          )}
          {onDelete && (
            <IconButton
              size="sm"
              variant="ghost"
              colorScheme="red"
              aria-label="Delete enrollment"
              onClick={() => onDelete(enrollment)}
            >
              <FiTrash2 />
            </IconButton>
          )}
        </Flex>
      </Stack>
    )
  },
)

EnrollmentCard.displayName = "EnrollmentCard"

export default EnrollmentCard
